import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Conversation, Message } from '@/types';
interface ConversationsState {
  // State
  conversations: Conversation[];
  currentConversationId: string | undefined;
  chatPageSelectedConversationId: string | undefined;
  loadingStates: Record<string, boolean>; // 为每个 projectId 维护独立的 loading 状态
  expandedProjectIds: Set<string>;
  sidebarProjectConversationsCache: Record<string, Conversation[]>;
  // 🔥 新增：防止同一会话在收到 message_init 前重复发送消息
  pendingMessageConversations: Set<string>;
  // Actions
  setConversations: (
    conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])
  ) => void;
  // 🔥 新增：消息发送锁定相关方法
  setPendingMessage: (conversationId: string) => void;
  clearPendingMessage: (conversationId: string) => void;
  hasPendingMessage: (conversationId: string) => boolean;
  // 🔥 新增：原子性地添加消息到会话，避免闭包导致的竞态条件
  addMessageToConversation: (
    conversationId: string,
    message: Message,
    extraProps?: Partial<Conversation>
  ) => boolean;
  setCurrentConversationId: (id: string | undefined) => void;
  setChatPageSelectedConversationId: (id: string | undefined) => void;
  setLoadingState: (key: string, isLoading: boolean) => void; // 设置特定 projectId 的 loading 状态
  addConversation: (conversation: Conversation) => void;
  removeConversation: (conversationId: string) => void;
  updateConversationTitle: (conversationId: string, title: string) => void;
  updateConversationInList: (
    conversationId: string,
    updater: (conv: Conversation) => Conversation
  ) => void;
  setExpandedProjectIds: (
    ids: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  projectExpand: (projectId: string) => void;
  setSidebarProjectConversationsCache: (
    cache:
      | Record<string, Conversation[]>
      | ((
          prev: Record<string, Conversation[]>
        ) => Record<string, Conversation[]>)
  ) => void;
}
export const useConversationsStore = create<ConversationsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      conversations: [],
      currentConversationId: undefined,
      chatPageSelectedConversationId: undefined,
      loadingStates: {},
      expandedProjectIds: new Set(),
      sidebarProjectConversationsCache: {},
      // 🔥 新增：防止同一会话在收到 message_init 前重复发送消息
      pendingMessageConversations: new Set(),
      // Actions
      setConversations: conversations => {
        set(state => {
          const newConversations =
            typeof conversations === 'function'
              ? conversations(state.conversations)
              : conversations;
          // 🔥 调试：检测消息丢失
          if (state.conversations.length > 0 && newConversations.length > 0) {
            const currentConv = state.conversations.find(
              c => c.messages && c.messages.length > 0
            );
            const newConv = newConversations.find(
              (c: any) => c.id === currentConv?.id
            );
            if (
              currentConv &&
              newConv &&
              currentConv.messages &&
              newConv.messages
            ) {
              if (newConv.messages.length < currentConv.messages.length) {
                console.error('🚨 消息丢失检测！', {
                  before: currentConv.messages.length,
                  after: newConv.messages.length,
                  convId: currentConv.id,
                });
                console.trace();
              }
            }
          }
          return { conversations: newConversations };
        });
      },
      setCurrentConversationId: id => {
        set({ currentConversationId: id });
      },
      setChatPageSelectedConversationId: id =>
        set({ chatPageSelectedConversationId: id }),
      setLoadingState: (key, isLoading) =>
        set(state => ({
          loadingStates: { ...state.loadingStates, [key]: isLoading },
        })),
      addConversation: conversation =>
        set(state => ({
          conversations: [conversation, ...state.conversations],
        })),
      // 🔥 新增：原子性地添加消息到会话，避免闭包导致的竞态条件
      addMessageToConversation: (conversationId, message, extraProps) => {
        let added = false;
        set(state => {
          const conversations = state.conversations.map(conv => {
            if (conv.id !== conversationId) return conv;
            const messages = conv.messages || [];
            // 检查消息是否已存在（通过 backendId 去重）
            if (
              message.backendId &&
              messages.some(m => m.backendId === message.backendId)
            ) {
              console.log(
                '🔔 Message already exists, skipping',
                message.backendId
              );
              return conv;
            }
            added = true;
            console.log('🔔 [Store] Adding message to conversation:', {
              convId: conversationId,
              messageId: message.backendId,
              existingCount: messages.length,
            });
            return {
              ...conv,
              messages: [...messages, message],
              ...extraProps,
            };
          });
          return { conversations };
        });
        return added;
      },
      removeConversation: conversationId =>
        set(state => ({
          conversations: state.conversations.filter(
            c => c.id !== conversationId
          ),
          currentConversationId:
            state.currentConversationId === conversationId
              ? undefined
              : state.currentConversationId,
        })),
      updateConversationTitle: (conversationId, title) =>
        set(state => ({
          conversations: state.conversations.map(c =>
            c.id === conversationId ? { ...c, title } : c
          ),
        })),
      updateConversationInList: (conversationId, updater) =>
        set(state => ({
          // 🔥 使用 String() 转换确保 ID 比较类型一致
          conversations: state.conversations.map(c =>
            String(c.id) === String(conversationId) ? updater(c) : c
          ),
        })),
      setExpandedProjectIds: ids =>
        set(state => ({
          expandedProjectIds:
            typeof ids === 'function' ? ids(state.expandedProjectIds) : ids,
        })),
      projectExpand: projectId =>
        set(state => {
          const newSet = new Set(state.expandedProjectIds);
          if (!newSet.has(projectId)) {
            newSet.add(projectId);
          }
          return { expandedProjectIds: newSet };
        }),
      setSidebarProjectConversationsCache: cache =>
        set(state => ({
          sidebarProjectConversationsCache:
            typeof cache === 'function'
              ? cache(state.sidebarProjectConversationsCache)
              : cache,
        })),
      // 🔥 新增：消息发送锁定相关方法
      setPendingMessage: conversationId =>
        set(state => {
          const newSet = new Set(state.pendingMessageConversations);
          newSet.add(conversationId);
          return { pendingMessageConversations: newSet };
        }),
      clearPendingMessage: conversationId =>
        set(state => {
          const newSet = new Set(state.pendingMessageConversations);
          newSet.delete(conversationId);
          return { pendingMessageConversations: newSet };
        }),
      hasPendingMessage: conversationId => {
        return get().pendingMessageConversations.has(conversationId);
      },
    }),
    { name: 'ConversationsStore' }
  )
);
